import { useMemo } from "react";

import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { useAppSelector } from "app/hooks";
import type { RootState } from "app/store";
import type { AssignedTag } from "services/types/assigned-tag.type";

// Tags changed on a call in this session, keyed by backend call id (ENG-2176).
// Same reason as `call-attachments.slice.ts`: the call lists accumulate rows in
// local `usePagination` state and are never invalidated (SN-18212), and a
// per-call invalidation would blank the detail panel on its refetch. So a tag
// add/remove records the call's new tag list here, and every surface showing
// that call reads it through `useCallTags`.
type CallTagsState = {
  byCallId: Record<number, AssignedTag[]>;
};

const initialState: CallTagsState = { byCallId: {} };

// The auth logout thunk's fulfilled action type; matched by string so this
// slice does not import the (import-heavy) auth slice.
const LOGOUT_FULFILLED = "auth/logout/fulfilled";

export const callTagsSlice = createSlice({
  name: "callTags",
  initialState,
  reducers: {
    callTagsChanged: (
      state,
      action: PayloadAction<{ callId: number; tags: AssignedTag[] }>
    ) => {
      state.byCallId[action.payload.callId] = action.payload.tags;
    },
    callTagsCleared: (state) => {
      state.byCallId = {};
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === LOGOUT_FULFILLED,
      (state) => {
        state.byCallId = {};
      }
    );
  }
});

export const { callTagsChanged, callTagsCleared } = callTagsSlice.actions;

export const selectCallTagsOverlay = (state: RootState) =>
  state.callTags.byCallId;

// One shared reference, so a call without tags hands `AssignedModelTags` the
// same array on every render (it re-syncs its local copy when the prop changes).
const NO_TAGS: AssignedTag[] = [];

export const callTagsFor = (
  call: { id: number; assigned_tags?: AssignedTag[] },
  overlay: Record<number, AssignedTag[]>
): AssignedTag[] => overlay[call.id] ?? call.assigned_tags ?? NO_TAGS;

// A call's tags as last changed in this session, else as the server sent them.
export function useCallTags(call: {
  id: number;
  assigned_tags?: AssignedTag[];
}): AssignedTag[] {
  const overlay = useAppSelector(selectCallTagsOverlay);
  const changed = overlay[call.id];
  const fetched = call.assigned_tags;
  return useMemo(() => changed ?? fetched ?? NO_TAGS, [changed, fetched]);
}

export default callTagsSlice.reducer;
